import React, { useRef, useCallback } from 'react';

export interface ResizeHandleProps {
	edge: 'top' | 'left';
	onResize: (delta: { dx: number; dy: number }) => void;
	onResizeEnd: () => void;
}

const baseStyle: React.CSSProperties = {
	position: 'absolute',
	zIndex: 11,
	touchAction: 'none',
	background: 'transparent',
	transition: 'background 0.15s ease',
};

const edgeStyles: Record<'top' | 'left', React.CSSProperties> = {
	top: {
		top: 0,
		left: 0,
		right: 0,
		height: '5px',
		cursor: 'ns-resize',
	},
	left: {
		top: 0,
		left: 0,
		bottom: 0,
		width: '5px',
		cursor: 'ew-resize',
	},
};

const hoverBg = 'rgba(9, 114, 211, 0.18)';

export const ResizeHandle: React.FC<ResizeHandleProps> = ({ edge, onResize, onResizeEnd }) => {
	const lastPos = useRef<{ x: number; y: number } | null>(null);

	const handlePointerMove = useCallback(
		(e: PointerEvent) => {
			if (!lastPos.current) return;
			const dx = e.clientX - lastPos.current.x;
			const dy = e.clientY - lastPos.current.y;
			lastPos.current = { x: e.clientX, y: e.clientY };
			onResize({ dx, dy });
		},
		[onResize],
	);

	const handlePointerUp = useCallback(
		(e: PointerEvent) => {
			const target = e.target as HTMLElement;
			target.releasePointerCapture(e.pointerId);
			target.removeEventListener('pointermove', handlePointerMove);
			target.removeEventListener('pointerup', handlePointerUp);
			lastPos.current = null;
			onResizeEnd();
		},
		[handlePointerMove, onResizeEnd],
	);

	const handlePointerDown = useCallback(
		(e: React.PointerEvent<HTMLDivElement>) => {
			e.preventDefault();
			const target = e.currentTarget;
			target.setPointerCapture(e.pointerId);
			lastPos.current = { x: e.clientX, y: e.clientY };
			target.addEventListener('pointermove', handlePointerMove);
			target.addEventListener('pointerup', handlePointerUp);
		},
		[handlePointerMove, handlePointerUp],
	);

	const handleMouseEnter = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
		e.currentTarget.style.background = hoverBg;
	}, []);

	const handleMouseLeave = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
		e.currentTarget.style.background = 'transparent';
	}, []);

	return (
		<div
			style={{ ...baseStyle, ...edgeStyles[edge] }}
			onPointerDown={handlePointerDown}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			aria-hidden="true"
		/>
	);
};
